import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { AGENTS_API_URL } from 'config';

const shellStyle = {
  background: '#f7f8fb',
  minHeight: '100vh',
  padding: '32px 16px',
};

const contentStyle = {
  margin: '0 auto',
  maxWidth: 1180,
};

const headerStyle = {
  marginBottom: 24,
};

const titleStyle = {
  color: '#1f2937',
  fontSize: 28,
  fontWeight: 700,
  lineHeight: 1.2,
  margin: '4px 0 8px',
};

const mutedStyle = {
  color: '#667085',
  fontSize: 14,
  lineHeight: 1.45,
  margin: 0,
};

const toolbarStyle = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  justifyContent: 'space-between',
  marginBottom: 16,
};

const tabsStyle = {
  background: '#ffffff',
  border: '1px solid #e5e7ef',
  borderRadius: 8,
  display: 'inline-flex',
  overflow: 'hidden',
};

const tabButtonStyle = (active) => ({
  background: active ? '#1f2937' : '#ffffff',
  border: 0,
  color: active ? '#ffffff' : '#344054',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 14,
  fontWeight: 700,
  minHeight: 40,
  padding: '0 14px',
});

const searchStyle = {
  background: '#ffffff',
  border: '1px solid #d5d7de',
  borderRadius: 8,
  boxSizing: 'border-box',
  font: 'inherit',
  minHeight: 42,
  minWidth: 260,
  padding: '0 12px',
};

const layoutStyle = {
  display: 'grid',
  gap: 16,
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
};

const panelStyle = {
  background: '#ffffff',
  border: '1px solid #e5e7ef',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
  minWidth: 0,
};

const threadButtonStyle = (active) => ({
  background: active ? '#f0f4ff' : '#ffffff',
  border: 0,
  borderBottom: '1px solid #edf0f5',
  cursor: 'pointer',
  display: 'block',
  font: 'inherit',
  padding: 16,
  textAlign: 'left',
  width: '100%',
});

const badgeStyle = {
  background: '#d92d20',
  borderRadius: 999,
  color: '#ffffff',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '20px',
  minWidth: 20,
  padding: '0 6px',
  textAlign: 'center',
};

const messageListStyle = {
  display: 'grid',
  gap: 10,
  maxHeight: 480,
  overflowY: 'auto',
  padding: 16,
};

const bubbleStyle = (own) => ({
  background: own ? '#e9f7ef' : '#f3f5fb',
  border: `1px solid ${own ? '#b7e4c7' : '#e1e6f0'}`,
  borderRadius: 8,
  justifySelf: own ? 'end' : 'start',
  maxWidth: '78%',
  padding: 12,
});

const composerStyle = {
  borderTop: '1px solid #edf0f5',
  display: 'grid',
  gap: 8,
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  padding: 16,
};

const textareaStyle = {
  border: '1px solid #d5d7de',
  borderRadius: 8,
  boxSizing: 'border-box',
  font: 'inherit',
  minHeight: 44,
  padding: 10,
  resize: 'vertical',
  width: '100%',
};

const primaryButtonStyle = {
  background: '#1f2937',
  border: '1px solid #1f2937',
  borderRadius: 8,
  color: '#ffffff',
  cursor: 'pointer',
  font: 'inherit',
  fontWeight: 700,
  minHeight: 42,
  padding: '0 14px',
};

const secondaryButtonStyle = {
  background: '#ffffff',
  border: '1px solid #d5d7de',
  borderRadius: 8,
  color: '#344054',
  cursor: 'pointer',
  font: 'inherit',
  fontWeight: 700,
  minHeight: 38,
  padding: '0 12px',
};

const requestJson = async (path, options = {}) => {
  const response = await fetch(`${AGENTS_API_URL}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
};

const modeConfig = {
  clients: {
    context: 'client',
    testId: 'local-chat-choose-client-page',
    title: 'Escolha um cliente',
    subtitle: 'Mensagens com clientes, visitas, propostas e documentos.',
  },
  owners: {
    context: 'owner',
    testId: 'local-owner-chat-page',
    title: 'Proprietarios',
    subtitle: 'Conversas com proprietarios e responsaveis por imovel.',
  },
  ownerHouse: {
    context: 'owner',
    testId: 'local-owner-house-chat-page',
    title: 'Conversa',
    subtitle: 'Canal interno com proprietario para combinar visita, chaves e entrada.',
  },
  thread: {
    context: 'all',
    testId: 'local-chat-thread-page',
    title: 'Conversa',
    subtitle: 'Thread criada pelo fluxo interno de atendimento.',
  },
  messages: {
    context: 'all',
    testId: 'local-messages-page',
    title: 'Mensagens',
    subtitle: 'Central de conversas do corretor dentro do APP2.',
  },
};

function buildConversationQuery({ context, houseId, search }) {
  const params = new URLSearchParams();
  if (context && context !== 'all') params.set('context', context);
  if (houseId) params.set('houseId', houseId);
  if (search) params.set('search', search);
  const query = params.toString();
  return `/chat/conversations${query ? `?${query}` : ''}`;
}

function formatTime(value) {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    month: '2-digit',
  }).format(new Date(value));
}

function contextLabel(context) {
  return context === 'owner' ? 'Proprietario' : 'Cliente';
}

function actionLabel(actionId) {
  const labels = {
    confirm_entry: 'Entrada',
    confirm_visit: 'Visita',
    request_documents: 'Documentos',
    request_keys: 'Chaves',
    send_offer: 'Proposta',
  };

  return labels[actionId] || 'Acao';
}

function defaultThreadIdFor(items, mode, activeContext) {
  if (!items.length) return null;

  const preferredContext =
    mode === 'messages' && activeContext === 'all' ? 'client' : activeContext;

  if (preferredContext === 'client' || preferredContext === 'owner') {
    const preferred = items.find((item) => item.context === preferredContext);
    if (preferred) return preferred.id;
  }

  return items[0].id;
}

const EmptyState = ({ text }) => (
  <div style={{ padding: 24 }}>
    <p style={mutedStyle}>{text}</p>
  </div>
);

EmptyState.propTypes = {
  text: PropTypes.string.isRequired,
};

const ThreadList = ({ activeThreadId, onSelectThread, threads }) => (
  <div style={panelStyle} data-testid="local-messages-thread-list">
    {threads.length === 0 ? (
      <EmptyState text="Nenhuma conversa encontrada." />
    ) : (
      threads.map((thread) => {
        const active = thread.id === activeThreadId;
        const lastMessage = thread.lastMessage?.body || 'Sem mensagens ainda.';
        return (
          <button
            aria-pressed={active}
            data-testid="local-message-thread"
            key={thread.id}
            onClick={() => onSelectThread(thread.id)}
            style={threadButtonStyle(active)}
            type="button"
          >
            <div style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
              <strong style={{ color: '#1f2937', flex: 1 }}>
                {thread.participant?.name || 'Contato'}
              </strong>
              {thread.unreadCount > 0 && (
                <span style={badgeStyle}>{thread.unreadCount}</span>
              )}
            </div>
            <p style={{ ...mutedStyle, marginTop: 4 }}>
              {contextLabel(thread.context)}
              {thread.house?.address ? ` - ${thread.house.address}` : ''}
            </p>
            <p
              style={{
                color: '#344054',
                fontSize: 14,
                lineHeight: 1.35,
                margin: '8px 0 0',
              }}
            >
              {lastMessage}
            </p>
          </button>
        );
      })
    )}
  </div>
);

ThreadList.propTypes = {
  activeThreadId: PropTypes.string,
  onSelectThread: PropTypes.func.isRequired,
  threads: PropTypes.array.isRequired,
};

const ThreadHeader = ({ thread }) => (
  <div style={{ borderBottom: '1px solid #edf0f5', padding: 16 }}>
    <div style={{ alignItems: 'flex-start', display: 'flex', gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ ...mutedStyle, fontWeight: 700 }}>
          {contextLabel(thread.context)}
        </p>
        <h2 style={{ ...titleStyle, fontSize: 22 }}>
          {thread.participant?.name || 'Contato'}
        </h2>
        <p style={mutedStyle}>
          {thread.house?.address || thread.participant?.phoneNumber || ''}
        </p>
      </div>
      {thread.unreadCount > 0 && (
        <span style={badgeStyle}>{thread.unreadCount}</span>
      )}
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
      {thread.visitorId && (
        <a href={`/client-profile/${thread.visitorId}`} style={secondaryButtonStyle}>
          Abrir perfil
        </a>
      )}
      {thread.houseId && (
        <a href={`/imovel/${thread.houseId}`} style={secondaryButtonStyle}>
          Ver imovel
        </a>
      )}
      {thread.participant?.phoneNumber && (
        <a href={`tel:${thread.participant.phoneNumber}`} style={secondaryButtonStyle}>
          Ligar
        </a>
      )}
    </div>
  </div>
);

ThreadHeader.propTypes = {
  thread: PropTypes.object.isRequired,
};

const ThreadMessages = ({ thread }) => (
  <div style={messageListStyle} data-testid="local-messages-thread-messages">
    {(thread.messages || []).map((message) => {
      const own = message.author === 'agent';
      return (
        <div key={message.id} style={bubbleStyle(own)}>
          <strong style={{ color: '#1f2937', display: 'block', fontSize: 13 }}>
            {message.authorName || (own ? 'Corretor' : 'Contato')}
          </strong>
          <p style={{ color: '#344054', fontSize: 15, lineHeight: 1.4, margin: '6px 0' }}>
            {message.body}
          </p>
          <span style={{ color: '#667085', fontSize: 12 }}>
            {formatTime(message.createdAt)}
          </span>
        </div>
      );
    })}
  </div>
);

ThreadMessages.propTypes = {
  thread: PropTypes.object.isRequired,
};

const ActionBar = ({ actions, busyActionId, onAction }) => {
  if (!actions.length) return null;

  return (
    <div
      style={{
        borderTop: '1px solid #edf0f5',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        padding: '12px 16px',
      }}
    >
      {actions.map((action) => (
        <button
          data-testid={`local-message-action-${action.id}`}
          disabled={busyActionId === action.id}
          key={action.id}
          onClick={() => onAction(action)}
          style={secondaryButtonStyle}
          type="button"
        >
          {busyActionId === action.id
            ? 'Enviando...'
            : action.label || actionLabel(action.id)}
        </button>
      ))}
    </div>
  );
};

ActionBar.propTypes = {
  actions: PropTypes.array.isRequired,
  busyActionId: PropTypes.string,
  onAction: PropTypes.func.isRequired,
};

const Composer = ({ disabled, onSubmit }) => {
  const [value, setValue] = useState('');

  const submit = (event) => {
    event.preventDefault();
    const body = value.trim();
    if (!body || disabled) return;
    onSubmit(body);
    setValue('');
  };

  return (
    <form onSubmit={submit} style={composerStyle}>
      <textarea
        aria-label="Mensagem"
        data-testid="local-message-composer"
        onChange={(event) => setValue(event.target.value)}
        placeholder="Digite uma mensagem"
        style={textareaStyle}
        value={value}
      />
      <button
        data-testid="local-message-send"
        disabled={disabled || !value.trim()}
        style={primaryButtonStyle}
        type="submit"
      >
        Enviar
      </button>
    </form>
  );
};

Composer.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

const ThreadPanel = ({
  busy,
  busyActionId,
  error,
  onAction,
  onSendMessage,
  thread,
}) => (
  <div style={panelStyle} data-testid="local-messages-thread-panel">
    {!thread && !error && <EmptyState text="Selecione uma conversa." />}
    {error && <EmptyState text={error} />}
    {thread && (
      <>
        <ThreadHeader thread={thread} />
        <ThreadMessages thread={thread} />
        <ActionBar
          actions={thread.actions || []}
          busyActionId={busyActionId}
          onAction={onAction}
        />
        <Composer disabled={busy} onSubmit={onSendMessage} />
      </>
    )}
  </div>
);

ThreadPanel.propTypes = {
  busy: PropTypes.bool.isRequired,
  busyActionId: PropTypes.string,
  error: PropTypes.string,
  onAction: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  thread: PropTypes.object,
};

const LocalMessagesPage = ({ houseId, mode, threadId }) => {
  const config = modeConfig[mode] || modeConfig.messages;
  const [activeContext, setActiveContext] = useState(config.context);
  const [activeThreadId, setActiveThreadId] = useState(threadId || null);
  const [busy, setBusy] = useState(false);
  const [busyActionId, setBusyActionId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [thread, setThread] = useState(null);
  const [threads, setThreads] = useState([]);

  const showAllTab = mode === 'messages' || mode === 'thread';
  const tabs = useMemo(
    () => [
      ...(showAllTab ? [{ id: 'all', label: 'Todas' }] : []),
      { id: 'client', label: 'Clientes' },
      { id: 'owner', label: 'Proprietarios' },
    ],
    [showAllTab],
  );

  useEffect(() => {
    if (threadId) {
      setActiveThreadId(threadId);
    }
  }, [threadId]);

  useEffect(() => {
    let isCurrent = true;
    const load = async () => {
      try {
        setLoading(true);
        const items = await requestJson(
          buildConversationQuery({
            context: activeContext,
            houseId,
            search,
          }),
        );
        if (!isCurrent) return;
        setThreads(items);
        setError(null);
        if (!activeThreadId && items[0]) {
          setActiveThreadId(defaultThreadIdFor(items, mode, activeContext));
        }
      } catch (requestError) {
        if (!isCurrent) return;
        setError(requestError.message || 'Nao foi possivel carregar conversas.');
        setThreads([]);
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    load();

    return () => {
      isCurrent = false;
    };
  }, [activeContext, activeThreadId, houseId, mode, search]);

  useEffect(() => {
    if (!activeThreadId) {
      setThread(null);
      return undefined;
    }

    let isCurrent = true;
    const loadThread = async () => {
      try {
        setBusy(true);
        const detail = await requestJson(`/chat/conversations/${activeThreadId}`);
        const readDetail = await requestJson(
          `/chat/conversations/${activeThreadId}/read`,
          { method: 'POST' },
        );
        if (!isCurrent) return;
        setThread(readDetail || detail);
        setError(null);
        setThreads((current) =>
          current.map((item) =>
            item.id === activeThreadId ? { ...item, unreadCount: 0 } : item,
          ),
        );
      } catch (requestError) {
        if (!isCurrent) return;
        setThread(null);
        setError(requestError.message || 'Nao foi possivel abrir a conversa.');
      } finally {
        if (isCurrent) setBusy(false);
      }
    };

    loadThread();

    return () => {
      isCurrent = false;
    };
  }, [activeThreadId]);

  const mergeThread = (nextThread) => {
    setThread(nextThread);
    setThreads((current) => {
      const nextSummary = {
        ...nextThread,
        messages: undefined,
        lastMessage: nextThread.messages?.[nextThread.messages.length - 1],
      };
      const existing = current.filter((item) => item.id !== nextThread.id);
      return [nextSummary, ...existing];
    });
  };

  const sendMessage = async (body) => {
    if (!thread) return;

    try {
      setBusy(true);
      const response = await requestJson(
        `/chat/conversations/${thread.id}/messages`,
        {
          body: JSON.stringify({ body }),
          method: 'POST',
        },
      );
      mergeThread(response.thread);
      setError(null);
    } catch (requestError) {
      setError(requestError.message || 'Nao foi possivel enviar a mensagem.');
    } finally {
      setBusy(false);
    }
  };

  const sendAction = async (action) => {
    if (!thread) return;

    try {
      setBusyActionId(action.id);
      const response = await requestJson(
        `/chat/conversations/${thread.id}/actions/${action.id}`,
        { method: 'POST' },
      );
      mergeThread(response.thread);
      setError(null);
    } catch (requestError) {
      setError(requestError.message || 'Nao foi possivel executar a acao.');
    } finally {
      setBusyActionId(null);
    }
  };

  return (
    <main style={shellStyle} data-testid={config.testId}>
      <div style={contentStyle}>
        <header style={headerStyle}>
          <p style={{ ...mutedStyle, fontWeight: 700 }}>Conversas</p>
          <h1 style={titleStyle}>{config.title}</h1>
          <p style={mutedStyle}>{config.subtitle}</p>
        </header>

        <div style={toolbarStyle}>
          <div style={tabsStyle} role="tablist" aria-label="Tipo de conversa">
            {tabs.map((tab) => (
              <button
                aria-selected={activeContext === tab.id}
                key={tab.id}
                onClick={() => {
                  setActiveContext(tab.id);
                  setActiveThreadId(null);
                }}
                role="tab"
                style={tabButtonStyle(activeContext === tab.id)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
          </div>
          <input
            aria-label="Buscar conversa"
            data-testid="local-message-search"
            onChange={(event) => {
              setSearch(event.target.value);
              setActiveThreadId(null);
            }}
            placeholder="Buscar por nome, imovel ou telefone"
            style={searchStyle}
            value={search}
          />
        </div>

        {loading && threads.length === 0 ? (
          <div style={panelStyle}>
            <EmptyState text="Carregando conversas..." />
          </div>
        ) : (
          <div style={layoutStyle}>
            <ThreadList
              activeThreadId={activeThreadId}
              onSelectThread={setActiveThreadId}
              threads={threads}
            />
            <ThreadPanel
              busy={busy}
              busyActionId={busyActionId}
              error={error}
              onAction={sendAction}
              onSendMessage={sendMessage}
              thread={thread}
            />
          </div>
        )}
      </div>
    </main>
  );
};

LocalMessagesPage.propTypes = {
  houseId: PropTypes.string,
  mode: PropTypes.oneOf(['clients', 'messages', 'owners', 'ownerHouse', 'thread']),
  threadId: PropTypes.string,
};

LocalMessagesPage.defaultProps = {
  houseId: null,
  mode: 'messages',
  threadId: null,
};

export default LocalMessagesPage;
